import { Eyebrow } from "@/components/typography/Eyebrow";
import { Heading } from "@/components/typography/Heading";
import { Text } from "@/components/typography/Text";
import { formatTreeSize, type PublicTestimonial } from "@/features/testimonials/testimonial";
import type { ReactElement } from "react";

interface TestimonialsProps {
  testimonials: PublicTestimonial[];
}

const sourceLabel: Record<PublicTestimonial["source"], string> = {
  app: "من التطبيق",
  web: "من الويب",
};

/** Consented words from Awraq families. The server sends nothing until at least three are published. */
export default function Testimonials({ testimonials }: TestimonialsProps): ReactElement | null {
  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section id="testimonials" className="py-12 md:py-16" dir="rtl" data-testid="landing-testimonials">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <Eyebrow>من العائلات</Eyebrow>
          <Heading level={2} className="mt-2">
            ماذا تقول العائلات عن أوراق
          </Heading>
          <Text size="small" className="mt-2">
            كلمات كتبها أصحابها من داخل أوراق، ونشرناها بموافقتهم.
          </Text>
        </div>

        <ul className="mt-8 flex flex-wrap justify-center gap-4">
          {testimonials.map((testimonial) => (
            <li
              key={`${testimonial.author}-${testimonial.body}`}
              className="w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc((100%-2rem)/3)]"
            >
              <figure className="flex h-full flex-col rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                <blockquote className="flex-1 text-sm leading-7 text-gray-900 md:text-base md:leading-8">
                  «{testimonial.body}»
                </blockquote>
                <figcaption className="mt-4 border-t border-gray-100 pt-3">
                  <p className="text-sm font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {testimonial.tree_size ? `${formatTreeSize(testimonial.tree_size)} · ` : null}
                    {sourceLabel[testimonial.source]}
                  </p>
                </figcaption>
              </figure>
            </li>
          ))}
        </ul>

        <Text size="helper" className="mt-6 text-center">
          لا ننشر أي رأي إلا بموافقة صاحبه، ويمكنه إخفاؤه متى شاء.
        </Text>
      </div>
    </section>
  );
}
